import React from "react"

import { OrderStatus, type Order } from "@/api/api-client"

import type { OrderTrackingPeriodFilter, OrderTrackingStatusFilter } from "./buyer-order-types"

interface UseOrderTrackingFiltersOptions {
  orders?: Order[] | null
  initialStatusFilter?: OrderTrackingStatusFilter
  initialPeriodFilter?: OrderTrackingPeriodFilter
  initialSearchQuery?: string
}

export function useOrderTrackingFilters({
  orders,
  initialStatusFilter = "all",
  initialPeriodFilter = "30d",
  initialSearchQuery = "",
}: UseOrderTrackingFiltersOptions) {
  const [statusFilter, setStatusFilter] =
    React.useState<OrderTrackingStatusFilter>(initialStatusFilter)
  const [periodFilter, setPeriodFilter] =
    React.useState<OrderTrackingPeriodFilter>(initialPeriodFilter)
  const [searchQuery, setSearchQuery] = React.useState(initialSearchQuery)

  const filteredOrders = React.useMemo(() => {
    if (!orders) return []

    return orders.filter((order) => {
      if (statusFilter === "shipped" && order.status !== OrderStatus.OrderShipped) return false
      if (
        statusFilter === "pending" &&
        (order.status === OrderStatus.OrderComplete ||
          order.status === OrderStatus.OrderShipped ||
          order.status === OrderStatus.OrderDeclined)
      ) {
        return false
      }

      const date = order.orderDate ? new Date(order.orderDate) : null
      if (date && !Number.isNaN(date.getTime())) {
        const now = new Date()
        if (periodFilter === "30d" && now.getTime() - date.getTime() > 30 * 86400000) return false
        if (periodFilter === "3m" && now.getTime() - date.getTime() > 90 * 86400000) return false
        if (periodFilter === "2026" && date.getFullYear() !== 2026) return false
        if (periodFilter === "2025" && date.getFullYear() !== 2025) return false
      }

      if (searchQuery && !String(order.id).includes(searchQuery.trim())) return false

      return true
    })
  }, [orders, statusFilter, periodFilter, searchQuery])

  return {
    filteredOrders,
    statusFilter,
    setStatusFilter,
    periodFilter,
    setPeriodFilter,
    searchQuery,
    setSearchQuery,
  }
}
